import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Ticket, Plus, Search, Filter, Copy, Archive, Power, Trash2,
  ChevronRight, X, Loader2, Check, AlertCircle, Eye, RefreshCw,
  DollarSign, Users, TrendingUp, Hash, Calendar, Tag, Percent,
  Shuffle
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled: { label: 'Planlagt', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  expired: { label: 'Utlopt', color: 'bg-stone-100 text-stone-500 border-stone-200' },
  disabled: { label: 'Deaktivert', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  exhausted: { label: 'Oppbrukt', color: 'bg-red-50 text-red-600 border-red-200' },
  archived: { label: 'Arkivert', color: 'bg-stone-100 text-stone-400 border-stone-200' },
};

const FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Activee' },
  { value: 'scheduled', label: 'Planlagte' },
  { value: 'expired', label: 'Utlopte' },
  { value: 'disabled', label: 'Deaktiverte' },
  { value: 'exhausted', label: 'Oppbrukte' },
];

const formatCurrency = (amount, currency) => {
  if (currency === 'nok') return `${amount?.toFixed(0) || 0} kr`;
  return `$${amount?.toFixed(2) || '0.00'}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

export const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [detailCoupon, setDetailCoupon] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchCoupons = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      if (typeFilter) params.set('coupon_type', typeFilter);
      const res = await axios.get(`${API}/coupons/admin/list?${params}`);
      setCoupons(res.data.coupons || []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, typeFilter]);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coupons/admin/analytics/overview`);
      setOverview(res.data);
    } catch (err) { console.error('Failed to fetch coupon overview:', err); }
  }, []);

  useEffect(() => { fetchCoupons(); fetchOverview(); }, [fetchCoupons, fetchOverview]);

  const handleToggle = async (couponId) => {
    setActionLoading(couponId);
    try {
      await axios.post(`${API}/coupons/admin/${couponId}/toggle`);
      fetchCoupons();
    } catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleArchive = async (couponId) => {
    setActionLoading(couponId);
    try {
      await axios.post(`${API}/coupons/admin/${couponId}/archive`);
      fetchCoupons();
      fetchOverview();
    } catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleDuplicate = async (couponId) => {
    setActionLoading(couponId);
    try {
      const res = await axios.post(`${API}/coupons/admin/${couponId}/duplicate`);
      fetchCoupons();
      fetchOverview();
      if (res.data.coupon) {
        setEditCoupon(res.data.coupon);
      }
    } catch (err) { console.error(err); }
    setActionLoading(null);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure? This action cannot be undone.')) return;
    setActionLoading(couponId);
    try {
      await axios.delete(`${API}/coupons/admin/${couponId}`);
      fetchCoupons();
      fetchOverview();
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete');
    }
    setActionLoading(null);
  };

  const openDetail = async (couponId) => {
    try {
      const res = await axios.get(`${API}/coupons/admin/${couponId}`);
      setDetailCoupon(res.data);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-6" data-testid="coupon-management">
      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="coupon-overview-stats">
          <MiniStat icon={Ticket} label="Activee coupons" value={overview.status_counts?.active || 0} />
          <MiniStat icon={Hash} label="Innlosninger" value={overview.total_redemptions} />
          <MiniStat icon={Users} label="Unike kunder" value={overview.unique_customers} />
          <MiniStat
            icon={DollarSign}
            label="Omsetning (kupong)"
            value={Object.entries(overview.revenue_by_currency || {}).map(([c, v]) => formatCurrency(v, c)).join(' / ') || '$0'}
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <input
              data-testid="coupon-search"
              type="text"
              placeholder="Search for coupon code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <select
            data-testid="coupon-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
          >
            {FILTER_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select
            data-testid="coupon-type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
          >
            <option value="">All typer</option>
            <option value="manual">Manuelle</option>
            <option value="recovery">Recovery</option>
          </select>
        </div>
        <Button
          data-testid="create-coupon-btn"
          onClick={() => setShowCreate(true)}
          className="bg-stone-900 text-white font-sans text-xs rounded-xl px-4 h-9"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Ny kupong
        </Button>
      </div>

      {/* Coupon List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-100" data-testid="coupon-empty-state">
          <Ticket className="w-8 h-8 text-stone-300 mx-auto mb-3" />
          <p className="font-sans text-sm font-medium text-stone-500">None coupons yet</p>
          <p className="font-sans text-xs text-stone-400 mt-1">Create your first campaign code to get started</p>
          <Button
            onClick={() => setShowCreate(true)}
            className="mt-4 bg-stone-900 text-white font-sans text-xs rounded-xl px-4 h-8"
          >
            <Plus className="w-3 h-3 mr-1" /> Create coupon
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="coupon-list">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100">
                  <th className="text-left px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Kode</th>
                  <th className="text-left px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Type</th>
                  <th className="text-left px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Rabatt</th>
                  <th className="text-left px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 hidden sm:table-cell">Gyldighet</th>
                  <th className="text-center px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Brukt</th>
                  <th className="text-left px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Status</th>
                  <th className="text-right px-4 py-3 font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr
                    key={c.id}
                    className="border-b border-stone-50 hover:bg-stone-50/50 transition-colors cursor-pointer"
                    data-testid={`coupon-row-${c.code}`}
                    onClick={() => openDetail(c.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs font-bold text-stone-800 bg-stone-50 px-2 py-0.5 rounded">{c.code}</code>
                        {c.coupon_type === 'recovery' && (
                          <span className="text-[9px] font-sans font-bold uppercase tracking-wider text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded">Recovery</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-sans text-xs text-stone-600">
                        {c.sitewide ? 'Hele katalogen' :
                          c.valid_subscriptions?.length ? 'Abonnement' :
                          c.valid_bundles?.length ? 'Pakke' :
                          c.valid_products?.length ? `${c.valid_products.length} product` :
                          'All'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-sans text-xs font-semibold text-stone-800">
                        {c.discount_type === 'percentage'
                          ? `${c.discount_value}%`
                          : formatCurrency(c.discount_value, c.currency || 'usd')
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="font-sans text-[10px] text-stone-400">
                        {c.start_date ? formatDate(c.start_date) : 'Immediately'}
                        {c.expiration_date ? ` — ${formatDate(c.expiration_date)}` : ' — None utlop'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-sans text-xs font-semibold text-stone-700">
                        {c.times_redeemed || 0}{c.max_redemptions ? `/${c.max_redemptions}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {actionLoading === c.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />
                        ) : (
                          <>
                            <button
                              data-testid={`toggle-${c.code}`}
                              onClick={() => handleToggle(c.id)}
                              className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
                              title={c.active ? 'Deaktiver' : 'Activeer'}
                            >
                              <Power className={`w-3.5 h-3.5 ${c.active ? 'text-emerald-500' : 'text-stone-400'}`} />
                            </button>
                            {c.coupon_type !== 'recovery' && (
                              <>
                                <button
                                  data-testid={`edit-${c.code}`}
                                  onClick={() => setEditCoupon(c)}
                                  className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
                                  title="Edit"
                                >
                                  <Tag className="w-3.5 h-3.5 text-stone-400" />
                                </button>
                                <button
                                  data-testid={`duplicate-${c.code}`}
                                  onClick={() => handleDuplicate(c.id)}
                                  className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
                                  title="Dupliser"
                                >
                                  <Copy className="w-3.5 h-3.5 text-stone-400" />
                                </button>
                              </>
                            )}
                            <button
                              data-testid={`archive-${c.code}`}
                              onClick={() => c.times_redeemed > 0 ? handleArchive(c.id) : handleDelete(c.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title={c.times_redeemed > 0 ? 'Archive' : 'Delete'}
                            >
                              {c.times_redeemed > 0
                                ? <Archive className="w-3.5 h-3.5 text-stone-400" />
                                : <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              }
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-stone-50 text-center">
            <p className="font-sans text-[10px] text-stone-400">
              Shows {coupons.length} coupons
            </p>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreate || editCoupon) && (
        <CouponFormModal
          coupon={editCoupon}
          onClose={() => { setShowCreate(false); setEditCoupon(null); }}
          onSaved={() => { setShowCreate(false); setEditCoupon(null); fetchCoupons(); fetchOverview(); }}
        />
      )}

      {/* Detail Modal */}
      {detailCoupon && (
        <CouponDetailModal
          coupon={detailCoupon}
          onClose={() => setDetailCoupon(null)}
        />
      )}
    </div>
  );
};

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.disabled;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-sans font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

/* ─── Mini Stat ─── */
const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl border border-stone-100 p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-6 h-6 rounded-md bg-stone-50 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-stone-500" />
      </div>
      <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400">{label}</span>
    </div>
    <p className="font-sans font-bold text-lg text-stone-900">{typeof value === 'number' ? value : value}</p>
  </div>
);

/* ─── Create/Edit Modal ─── */
const CouponFormModal = ({ coupon, onClose, onSaved }) => {
  const isEdit = !!coupon;
  const [form, setForm] = useState({
    code: coupon?.code || '',
    autoCode: false,
    discount_type: coupon?.discount_type || 'percentage',
    discount_value: coupon?.discount_value || 10,
    currency: coupon?.currency || 'usd',
    sitewide: coupon?.sitewide ?? true,
    valid_products: coupon?.valid_products || [],
    valid_bundles: coupon?.valid_bundles || [],
    valid_subscriptions: coupon?.valid_subscriptions || [],
    eligibility: coupon ? (
      coupon.sitewide ? 'all' :
      coupon.valid_subscriptions?.length ? 'subscription' :
      coupon.valid_bundles?.length ? 'bundle' :
      coupon.valid_products?.length ? 'product' : 'all'
    ) : 'all',
    min_purchase: coupon?.min_purchase || '',
    min_purchase_currency: coupon?.min_purchase_currency || 'usd',
    max_redemptions: coupon?.max_redemptions || '',
    max_per_customer: coupon?.max_per_customer || 1,
    start_date: coupon?.start_date ? coupon.start_date.slice(0, 16) : '',
    expiration_date: coupon?.expiration_date ? coupon.expiration_date.slice(0, 16) : '',
    active: coupon?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [productInput, setProductInput] = useState('');
  const [bundleInput, setBundleInput] = useState('');

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!isEdit && !form.autoCode && !form.code.trim()) {
      setError('Kupongkode er pakreved');
      return;
    }
    if (form.discount_value <= 0) {
      setError('Rabattverdi ma vaere storre enn 0');
      return;
    }
    if (form.discount_type === 'percentage' && (form.discount_value < 1 || form.discount_value > 99)) {
      setError('Prosent ma vaere mellom 1 og 99');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        currency: form.discount_type === 'fixed' ? form.currency : null,
        sitewide: form.eligibility === 'all',
        valid_products: form.eligibility === 'product' ? form.valid_products : null,
        valid_bundles: form.eligibility === 'bundle' ? form.valid_bundles : null,
        valid_subscriptions: form.eligibility === 'subscription' ? form.valid_subscriptions : null,
        min_purchase: form.min_purchase ? parseFloat(form.min_purchase) : null,
        min_purchase_currency: form.min_purchase ? form.min_purchase_currency : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        max_per_customer: form.max_per_customer ? parseInt(form.max_per_customer) : null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        expiration_date: form.expiration_date ? new Date(form.expiration_date).toISOString() : null,
        active: form.active,
      };

      if (isEdit) {
        await axios.put(`${API}/coupons/admin/${coupon.id}`, payload);
      } else {
        payload.code = form.autoCode ? null : form.code.trim().toUpperCase();
        await axios.post(`${API}/coupons/admin/create`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setSaving(false);
  };

  const addProduct = () => {
    const slug = productInput.trim();
    if (slug && !form.valid_products.includes(slug)) {
      updateField('valid_products', [...form.valid_products, slug]);
    }
    setProductInput('');
  };

  const addBundle = () => {
    const slug = bundleInput.trim();
    if (slug && !form.valid_bundles.includes(slug)) {
      updateField('valid_bundles', [...form.valid_bundles, slug]);
    }
    setBundleInput('');
  };

  // Build preview data
  const preview = {
    code: isEdit ? coupon.code : (form.autoCode ? '(Auto-generert)' : form.code.toUpperCase() || '—'),
    discount: form.discount_type === 'percentage'
      ? `${form.discount_value}% rabatt`
      : `${formatCurrency(form.discount_value, form.currency)} rabatt`,
    applies: form.eligibility === 'all' ? 'Hele katalogen'
      : form.eligibility === 'product' ? `${form.valid_products.length} product(er)`
      : form.eligibility === 'bundle' ? `${form.valid_bundles.length} pakke(r)`
      : form.valid_subscriptions.join(', ') || 'Abonnement',
    validity: `${form.start_date ? formatDateTime(form.start_date) : 'Immediately'} — ${form.expiration_date ? formatDateTime(form.expiration_date) : 'None utlop'}`,
    maxUses: form.max_redemptions || 'Ubegrenset',
    perCustomer: form.max_per_customer ? `Maks ${form.max_per_customer} per kunde` : 'Ubegrenset',
    minPurchase: form.min_purchase ? `${form.min_purchase} ${(form.min_purchase_currency || 'usd').toUpperCase()}` : 'None',
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="coupon-form-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <h2 className="font-serif font-bold text-lg text-stone-900">
            {isEdit ? 'Edit coupon' : 'Create coupon'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Code */}
          {!isEdit && (
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Kupongkode</label>
              <div className="flex gap-2">
                <input
                  data-testid="coupon-code-input"
                  type="text"
                  placeholder="e.g. WELCOME10"
                  value={form.code}
                  onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                  disabled={form.autoCode}
                  className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
                />
                <button
                  data-testid="auto-code-btn"
                  onClick={() => updateField('autoCode', !form.autoCode)}
                  className={`px-3 py-2 rounded-xl border text-xs font-sans flex items-center gap-1.5 transition-colors ${
                    form.autoCode ? 'bg-stone-900 text-white border-stone-900' : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <Shuffle className="w-3 h-3" /> Auto
                </button>
              </div>
            </div>
          )}

          {/* Discount Type & Value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Rabattype</label>
              <select
                data-testid="discount-type-select"
                value={form.discount_type}
                onChange={(e) => updateField('discount_type', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              >
                <option value="percentage">Prosent (%)</option>
                <option value="fixed">Fast belop</option>
              </select>
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">
                {form.discount_type === 'percentage' ? 'Prosent' : 'Belop'}
              </label>
              <div className="flex gap-2">
                <input
                  data-testid="discount-value-input"
                  type="number"
                  min="0"
                  step={form.discount_type === 'percentage' ? '1' : '0.01'}
                  value={form.discount_value}
                  onChange={(e) => updateField('discount_value', e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                {form.discount_type === 'fixed' && (
                  <select
                    data-testid="discount-currency-select"
                    value={form.currency}
                    onChange={(e) => updateField('currency', e.target.value)}
                    className="px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
                  >
                    <option value="usd">USD</option>
                    <option value="nok">NOK</option>
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Eligibility */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Gjelder for</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'all', label: 'Hele katalogen' },
                { value: 'product', label: 'Spesifikke products' },
                { value: 'bundle', label: 'Pakker/bundles' },
                { value: 'subscription', label: 'Abonnement' },
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`eligibility-${opt.value}`}
                  onClick={() => updateField('eligibility', opt.value)}
                  className={`px-3 py-2 rounded-xl border text-xs font-sans transition-colors ${
                    form.eligibility === opt.value
                      ? 'bg-stone-900 text-white border-stone-900'
                      : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Product slugs input */}
            {form.eligibility === 'product' && (
              <div className="mt-3">
                <div className="flex gap-2">
                  <input
                    data-testid="product-slug-input"
                    placeholder="Product-slug (e.g. wedding-planner)"
                    value={productInput}
                    onChange={(e) => setProductInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addProduct())}
                    className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
                  />
                  <Button onClick={addProduct} variant="outline" className="text-xs h-auto py-2 rounded-xl">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.valid_products.map(p => (
                    <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 rounded-full text-[10px] font-sans text-stone-600">
                      {p}
                      <button onClick={() => updateField('valid_products', form.valid_products.filter(x => x !== p))}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bundle slugs input */}
            {form.eligibility === 'bundle' && (
              <div className="mt-3">
                <div className="flex gap-2">
                  <input
                    data-testid="bundle-slug-input"
                    placeholder="Pakke-slug"
                    value={bundleInput}
                    onChange={(e) => setBundleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBundle())}
                    className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
                  />
                  <Button onClick={addBundle} variant="outline" className="text-xs h-auto py-2 rounded-xl">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.valid_bundles.map(b => (
                    <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 rounded-full text-[10px] font-sans text-stone-600">
                      {b}
                      <button onClick={() => updateField('valid_bundles', form.valid_bundles.filter(x => x !== b))}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription selection */}
            {form.eligibility === 'subscription' && (
              <div className="mt-3 flex gap-2">
                {[
                  { value: 'pro', label: 'Pro Manedlig' },
                  { value: 'pro_yearly', label: 'Pro Arlig' },
                ].map(s => (
                  <button
                    key={s.value}
                    data-testid={`sub-${s.value}`}
                    onClick={() => {
                      const current = form.valid_subscriptions;
                      updateField('valid_subscriptions',
                        current.includes(s.value)
                          ? current.filter(x => x !== s.value)
                          : [...current, s.value]
                      );
                    }}
                    className={`px-3 py-2 rounded-xl border text-xs font-sans transition-colors ${
                      form.valid_subscriptions.includes(s.value)
                        ? 'bg-stone-900 text-white border-stone-900'
                        : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Validity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Startdato</label>
              <input
                data-testid="start-date-input"
                type="datetime-local"
                value={form.start_date}
                onChange={(e) => updateField('start_date', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              />
              <p className="font-sans text-[10px] text-stone-400 mt-0.5">Empty = active immediately</p>
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Utlopsdato</label>
              <input
                data-testid="expiration-date-input"
                type="datetime-local"
                value={form.expiration_date}
                onChange={(e) => updateField('expiration_date', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              />
              <p className="font-sans text-[10px] text-stone-400 mt-0.5">Tom = ingen utlop</p>
            </div>
          </div>

          {/* Usage Limits */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Maks innlosninger</label>
              <input
                data-testid="max-redemptions-input"
                type="number"
                min="0"
                placeholder="Ubegrenset"
                value={form.max_redemptions}
                onChange={(e) => updateField('max_redemptions', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Per kunde</label>
              <input
                data-testid="max-per-customer-input"
                type="number"
                min="0"
                placeholder="Ubegrenset"
                value={form.max_per_customer}
                onChange={(e) => updateField('max_per_customer', e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              />
            </div>
          </div>

          {/* Min Purchase */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-700 block mb-1.5">Minimum purchase</label>
            <div className="flex gap-2">
              <input
                data-testid="min-purchase-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="None minimum"
                value={form.min_purchase}
                onChange={(e) => updateField('min_purchase', e.target.value)}
                className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
              />
              {form.min_purchase && (
                <select
                  data-testid="min-purchase-currency"
                  value={form.min_purchase_currency}
                  onChange={(e) => updateField('min_purchase_currency', e.target.value)}
                  className="px-3 py-2 rounded-xl border border-stone-200 text-xs font-sans bg-white focus:outline-none"
                >
                  <option value="usd">USD</option>
                  <option value="nok">NOK</option>
                </select>
              )}
            </div>
          </div>

          {/* Preview */}
          <div>
            <button
              data-testid="toggle-preview-btn"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 font-sans text-xs font-semibold text-stone-600 hover:text-stone-800"
            >
              <Eye className="w-3.5 h-3.5" />
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>

            {showPreview && (
              <div className="mt-3 bg-stone-50 rounded-xl p-4 space-y-2 border border-stone-100" data-testid="coupon-preview">
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Kode</span>
                  <code className="font-mono text-xs font-bold text-stone-800">{preview.code}</code>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Rabatt</span>
                  <span className="font-sans text-xs font-semibold text-stone-700">{preview.discount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Gjelder</span>
                  <span className="font-sans text-xs text-stone-600">{preview.applies}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Gyldighet</span>
                  <span className="font-sans text-xs text-stone-600">{preview.validity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Maks bruk</span>
                  <span className="font-sans text-xs text-stone-600">{preview.maxUses}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Per kunde</span>
                  <span className="font-sans text-xs text-stone-600">{preview.perCustomer}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Min. purchase</span>
                  <span className="font-sans text-xs text-stone-600">{preview.minPurchase}</span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-2.5" data-testid="coupon-form-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="font-sans text-xs">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white z-10 px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            className="font-sans text-xs"
          >
            Cancel
          </Button>
          <Button
            data-testid="save-coupon-btn"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-stone-900 text-white font-sans text-xs rounded-xl px-5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            {isEdit ? 'Save changes' : 'Create coupon'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Detail Modal ─── */
const CouponDetailModal = ({ coupon, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="coupon-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm font-bold text-stone-900">{coupon.code}</code>
              <StatusBadge status={coupon.status} />
              {coupon.coupon_type === 'recovery' && (
                <span className="text-[9px] font-sans font-bold uppercase tracking-wider text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded">Recovery</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Config */}
          <Section title="Konfigurasjon">
            <DetailRow label="Rabatt" value={
              coupon.discount_type === 'percentage'
                ? `${coupon.discount_value}%`
                : formatCurrency(coupon.discount_value, coupon.currency || 'usd')
            } />
            <DetailRow label="Type" value={coupon.discount_type === 'percentage' ? 'Prosent' : 'Fast belop'} />
            {coupon.currency && <DetailRow label="Valuta" value={coupon.currency.toUpperCase()} />}
            <DetailRow label="Gjelder for" value={
              coupon.sitewide ? 'Hele katalogen' :
              coupon.valid_subscriptions?.length ? coupon.valid_subscriptions.join(', ') :
              coupon.valid_bundles?.length ? coupon.valid_bundles.join(', ') :
              coupon.valid_products?.length ? coupon.valid_products.join(', ') :
              'All'
            } />
            <DetailRow label="Maks bruk" value={coupon.max_redemptions || 'Ubegrenset'} />
            <DetailRow label="Per kunde" value={coupon.max_per_customer || 'Ubegrenset'} />
            {coupon.min_purchase > 0 && (
              <DetailRow label="Min. purchase" value={`${coupon.min_purchase} ${(coupon.min_purchase_currency || 'usd').toUpperCase()}`} />
            )}
          </Section>

          {/* Dates */}
          <Section title="Gyldighet">
            <DetailRow label="Created" value={formatDateTime(coupon.created_at)} />
            <DetailRow label="Oppdatert" value={formatDateTime(coupon.updated_at)} />
            <DetailRow label="Start" value={coupon.start_date ? formatDateTime(coupon.start_date) : 'Immediately'} />
            <DetailRow label="Utlop" value={coupon.expiration_date ? formatDateTime(coupon.expiration_date) : 'None utlop'} />
            {coupon.created_by && <DetailRow label="Created av" value={coupon.created_by} />}
          </Section>

          {/* Performance */}
          <Section title="Ytelse">
            <DetailRow label="Innlosninger" value={`${coupon.times_redeemed || 0}${coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : ''}`} />
            <DetailRow label="Unike kunder" value={coupon.unique_customers || 0} />
            {coupon.analytics_by_currency && Object.entries(coupon.analytics_by_currency).map(([curr, data]) => (
              <React.Fragment key={curr}>
                <DetailRow label={`Omsetning (${curr.toUpperCase()})`} value={formatCurrency(data.revenue, curr)} />
                <DetailRow label={`Rabatt gitt (${curr.toUpperCase()})`} value={formatCurrency(data.discount, curr)} />
                {data.count > 0 && (
                  <DetailRow label={`AOV (${curr.toUpperCase()})`} value={formatCurrency(data.revenue / data.count, curr)} />
                )}
              </React.Fragment>
            ))}
          </Section>

          {/* Redemption History */}
          {coupon.redemptions?.length > 0 && (
            <Section title={`Innlosningshistorikk (${coupon.redemptions.length})`}>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {coupon.redemptions.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-50 last:border-0">
                    <div>
                      <p className="font-sans text-xs text-stone-700">{r.user_email}</p>
                      <p className="font-sans text-[10px] text-stone-400">
                        {formatDateTime(r.created_at)}
                        {r.product_slug && ` — ${r.product_slug}`}
                        {r.bundle_slug && ` — ${r.bundle_slug}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-sans text-xs font-semibold text-emerald-600">-{formatCurrency(r.discount_amount, r.currency)}</p>
                      <p className="font-sans text-[10px] text-stone-400">{formatCurrency(r.final_amount, r.currency)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Audit Log */}
          {coupon.audit_log?.length > 0 && (
            <Section title="Endringslogg">
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {coupon.audit_log.slice().reverse().map((entry, i) => (
                  <p key={i} className="font-sans text-[10px] text-stone-400">
                    <span className="font-semibold text-stone-500">{entry.action}</span>
                    {' av '}{entry.by}{' — '}{formatDateTime(entry.at)}
                    {entry.fields && ` (${entry.fields.join(', ')})`}
                    {entry.source && ` fra ${entry.source}`}
                  </p>
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">{title}</h3>
    <div className="bg-stone-50 rounded-xl p-3 space-y-1.5">{children}</div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-start">
    <span className="font-sans text-[10px] text-stone-400">{label}</span>
    <span className="font-sans text-xs text-stone-700 text-right max-w-[60%] break-words">{value}</span>
  </div>
);
